import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { i18n } from '~/utils';

import {
  type ParsingCaseNoteResponse,
  type SaveParsingCaseNoteVariables,
} from '../types';
import { useParsingCaseActionMutationOptions } from '../useParsingCaseActionMutationOptions';

import { saveParsingCaseNote } from './saveParsingCaseNote';

/**
 * Custom hook to save a parsing case note without retrying the case.
 *
 * @param options - Optional react-query mutation options
 * @returns The mutation result
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases/operation/put_parsing_case_note_vadmin_parsing_cases__case_id__note_put
 */
export const useMutationSaveParsingCaseNote = (
  options?: UseMutationOptions<
    ParsingCaseNoteResponse,
    unknown,
    SaveParsingCaseNoteVariables
  >,
) => {
  const actionOptions =
    useParsingCaseActionMutationOptions<SaveParsingCaseNoteVariables>(() =>
      i18n.t(($) => $.saveParsingCaseNote.success, { ns: 'data' }),
    );

  return useMutation<
    ParsingCaseNoteResponse,
    unknown,
    SaveParsingCaseNoteVariables
  >({
    mutationFn: async (variables) => saveParsingCaseNote(variables),
    ...actionOptions,
    ...options,
  });
};
